import React, { useEffect, useRef, useState } from 'react';
import { SorterResult } from 'antd/es/table/interface';
import { Col, Divider, Popconfirm, Popover, Row, Select, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { EyeOutlined, CopyOutlined, DeleteOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react';
import { isArray } from 'lodash';
import { IOfferHistoryItem } from '../../../../Types/Interfaces/OfferInterfaces';
import { SWEditOfferTemplateDrawer } from '../../../../Components/Composite';
import { offerStore } from '../../../../GlobalStore';
import { SWButton, SWInput, SWDatePicker } from '../../../../Components/UI';
import { OfferHistorySortTypeEnums } from '../../../../Types/Enums/OfferHistorySortTypeEnums';
import { OfferHistorySortOrderEnums } from '../../../../Types/Enums/OfferHistorySortOrderEnums';
import styles from './index.module.scss';

const RegisteredOffersPage = observer(() => {
  const [copiedOffer, setCopiedOffer] = useState<IOfferHistoryItem>();
  const [selectedOffer, setSelectedOffer] = useState<IOfferHistoryItem>();
  const [searchValue, setSearchValue] = useState<string>("");
  const [insuranceTypeFilter, setInsuranceTypeFilter] = useState<string>("");
  const [createDateFilter, setCreateDateFilter] = useState<string>("");
  const [validityDateFilter, setValidityDateFilter] = useState<string>("");
  const [sortField, setSortField] = useState<OfferHistorySortTypeEnums | undefined>();
  const [sortOrder, setSortOrder] = useState<OfferHistorySortOrderEnums | undefined>();
  const sortParamsRef = useRef<{
    field?: OfferHistorySortTypeEnums;
    order?: OfferHistorySortOrderEnums;
  }>({
    field: undefined,
    order: undefined,
  });
  const [page, setPage] = useState<number | undefined>();
  const [pageSize, setPageSize] = useState<number | undefined>();
  const [totalPage, setTotalPage] = useState<number | undefined>();

  useEffect(() => {
    offerStore.fillOfferHistory(offerStore?.offerHistory.current_page, offerStore?.offerHistory.page_size);
    offerStore.fillOfferHistoryInsuranceTypes();
  }, []);

  const getHistoryByTableSorter = (
    sorter: SorterResult<IOfferHistoryItem> | SorterResult<IOfferHistoryItem>[],
    nextPage: number = 1,
    nextPageSize: number = 10,
    action?: 'paginate' | 'sort' | 'filter'
  ) => {
    if (!isArray(sorter)) {
      const isPaginationChange = nextPage !== page || nextPageSize !== pageSize;
      const isSortChange = action === 'sort' && !isPaginationChange;
      const nextSortField = isSortChange
        ? sorter.order
          ? (sorter.columnKey as OfferHistorySortTypeEnums | undefined) || sortField
          : undefined
        : sortParamsRef.current.field;
      const nextSortOrder = isSortChange
        ? sorter.order === 'ascend'
          ? OfferHistorySortOrderEnums.ASC
          : sorter.order === 'descend'
            ? OfferHistorySortOrderEnums.DESC
            : undefined
        : sortParamsRef.current.order;
      const requestPage = isSortChange ? 1 : nextPage;

      sortParamsRef.current = {
        field: nextSortField,
        order: nextSortOrder,
      };
      offerStore.fillOfferHistory(requestPage, nextPageSize, nextSortField, nextSortOrder, searchValue, insuranceTypeFilter, createDateFilter, validityDateFilter);
      setSortField(nextSortField);
      setSortOrder(nextSortOrder);
      setPage(requestPage || 1);
      setPageSize(nextPageSize || 10);
    }
  };

  const copyOffer = (offer: IOfferHistoryItem) => {
    setCopiedOffer(offer);
  };

  const handleChangeSearch = (value: string): void => setSearchValue(value);
  const handleChangeInsuranceType = (value?: string): void => setInsuranceTypeFilter(value || "");
  const handleSelectCreateDate = (date: string): void => setCreateDateFilter(date);
  const handleSelectValidityDate = (date: string): void => setValidityDateFilter(date);

  const handleClickFilterButton = (): void => {
    sortParamsRef.current = {
      field: undefined,
      order: undefined,
    };
    setSortField(undefined);
    setSortOrder(undefined);
    offerStore.fillOfferHistory(1, 10, undefined, undefined, searchValue, insuranceTypeFilter, createDateFilter, validityDateFilter);
  }

  const getAntdSortOrder = (field: OfferHistorySortTypeEnums) => {
    if (sortField !== field) return undefined;

    return sortOrder === OfferHistorySortOrderEnums.ASC ? 'ascend' : 'descend';
  };

  const tableCols: ColumnsType<IOfferHistoryItem> = [
    {
      title: 'Müşteri',
      key: OfferHistorySortTypeEnums.USER,
      dataIndex: 'customer',
      render: (value, record) => {
        const customer = record?.offer_inside?.user_info;
        return customer?.isim_soyisim || customer?.unvan;
      },
      sorter: true,
      sortOrder: getAntdSortOrder(OfferHistorySortTypeEnums.USER)
    },
    {
      title: 'Sigorta Türü',
      key: OfferHistorySortTypeEnums.TYPE_NAME,
      dataIndex: 'offerType',
      render: (_, record) => record.offer_inside?.type_name,
      sorter: true,
      sortOrder: getAntdSortOrder(OfferHistorySortTypeEnums.TYPE_NAME)
    },
    {
      title: 'Oluşturulma Tarihi',
      key: OfferHistorySortTypeEnums.OFFER_DATE,
      dataIndex: 'createDate',
      render: (value, record) => record?.offer_inside?.offerDate || '-',
      sorter: true,
      sortOrder: getAntdSortOrder(OfferHistorySortTypeEnums.OFFER_DATE)
    },
    {
      title: 'Geçerlilik Tarihi',
      key: OfferHistorySortTypeEnums.OFFER_VALIDITY_DATE,
      dataIndex: 'validityDate',
      render: (value, record) => record?.offer_inside?.offerValidityDate || '-',
      sorter: true,
      sortOrder: getAntdSortOrder(OfferHistorySortTypeEnums.OFFER_VALIDITY_DATE)
    },
    {
      title: 'İşlem',
      key: 'actions',
      dataIndex: 'actions',
      align: 'right',
      render: (_, record) => (
        <Row gutter={[10, 10]} justify="end">
          <Col>
            <Popover content="Görüntüle" trigger="hover">
              <EyeOutlined onClick={() => setSelectedOffer(record)} />
            </Popover>
          </Col>
          <Col>
            <Popover content="Kopyala" trigger="hover">
              <CopyOutlined onClick={() => copyOffer(record)} />
            </Popover>
          </Col>
          <Col>
            <Popconfirm
              title="Silmek istediğinize emin misiniz?"
              okText="Sil"
              cancelText="Vazgeç"
              onConfirm={() => offerStore.deleteOfferHistory(record?.id)}>
              <Popover content="Sil" trigger="hover">
                <DeleteOutlined />
              </Popover>
            </Popconfirm>
          </Col>
        </Row>
      )
    }
  ];

  useEffect(() => {
    if (!offerStore.offerHistoryLoading) {
      const { current_page, page_size, total_pages } = offerStore?.offerHistory;
      console.log(total_pages);
      setPage(current_page);
      setPageSize(page_size);
      setTotalPage(total_pages);
    }
  }, [offerStore.offerHistoryLoading]);

  return (
    <div>
      <Divider>
        <h2>Geçmiş Teklifler</h2>
      </Divider>
      <div className={styles.buttonContainer}>
        <div className={styles.input}>
          <SWInput
            id="searchInput"
            value={searchValue}
            onChange={handleChangeSearch}
            placeholder="Müşteri"
            type='text'
            height={34}
            style={{ border: { borderWidth: 1 }, fontSize: 12 }}
          />
        </div>
        <div className={styles.input}>
          <Select
            allowClear
            showSearch
            value={insuranceTypeFilter || undefined}
            onChange={handleChangeInsuranceType}
            placeholder="Sigorta Türü"
            loading={offerStore.offerHistoryInsuranceTypesLoading}
            options={offerStore.offerHistoryInsuranceTypes.map((insuranceType) => ({
              label: insuranceType,
              value: insuranceType,
            }))}
            optionFilterProp="label"
            style={{ width: '100%', height: 34 }}
          />
        </div>
        <SWDatePicker
          label="Oluşturulma Tarihi"
          width={200}
          onSelect={handleSelectCreateDate}
        />
        <SWDatePicker
          label="Geçerlilik Tarihi"
          width={200}
          onSelect={handleSelectValidityDate}
        />
        <SWButton
          disabled={offerStore.offerHistoryLoading}
          content='FİLTRELE'
          size='middle'
          type='primary'
          onClick={handleClickFilterButton}
        />
      </div>
      <Table
        columns={tableCols}
        dataSource={[...offerStore?.offerHistory?.offers] || []}
        rowKey={(i) => i.offer_inside.id}
        loading={offerStore.offerHistoryLoading}
        pagination={{ current: page, pageSize: pageSize, total: (typeof totalPage === 'number' && typeof pageSize === 'number') ? totalPage * pageSize : 1 }}
        onChange={(pagination, filters, sorter, extra) => {
          getHistoryByTableSorter(sorter, pagination.current, pagination.pageSize, extra.action);
        }}
      />
      <SWEditOfferTemplateDrawer
        isOfferHistory
        refNo={selectedOffer?.ref_no}
        creator={selectedOffer?.creator}
        offerType={selectedOffer?.offer_inside}
        visible={typeof selectedOffer !== 'undefined'}
        onClose={() => setSelectedOffer(undefined)}
        title={`${selectedOffer?.ref_no || ''} - Geçmiş Teklif`}
      />
      <SWEditOfferTemplateDrawer
        isCopyOffer
        customerId={copiedOffer?.customer_id}
        offerType={copiedOffer?.offer_inside}
        visible={typeof copiedOffer !== 'undefined'}
        onClose={() => setCopiedOffer(undefined)}
        title={`Teklif Kopyala - ${copiedOffer?.offer_inside?.type_name} Sigortası`}
      />
    </div>
  );
});

export default RegisteredOffersPage;
